// src/components/founderDashboard/InsightsDashboard.tsx
// Founder user-behaviour insights: engagement, screens and paths, funnels,
// retention cohorts, programme performance and what notifications do.
// All numbers come from /founder-portal/insights/ (founder_portal/insights.py),
// computed from our own request log, app events, DailyActive and domain rows.
import React, { useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, ComposedChart, BarChart, Bar, Line, Sankey, Cell, LabelList,
    XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "./apiClient";
import {
    C, FONT, numStyle, microLabel, fmt, shortDate, discColor, ngroupColor,
    RangePicker, RangeSel, LIVE_DATE, Shell, KpiStrip, Card, Grid, Split, SectionHead, SectionNav,
    Legend, BarRow, Empty, axisX, axisY, gridProps, tipBar, th, td, tdNum, TableWrap,
} from "./hq";

type Row = Record<string, any>;

const iso = (d: Date) => d.toISOString().slice(0, 10);

// "SingleWorkoutSummary" -> "Single workout summary"; request areas pass through.
const pretty = (s: string) => {
    if (/\s/.test(s)) return s;
    const spaced = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return spaced.charAt(0) + spaced.slice(1).toLowerCase();
};

const dur = (secs: number | null | undefined) => {
    if (secs === null || secs === undefined) return "-";
    if (secs < 60) return `${Math.round(secs)}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${String(Math.round(secs % 60)).padStart(2, "0")}s`;
    return `${(secs / 3600).toFixed(1)}h`;
};
const hrs = (h: number | null | undefined) => {
    if (h === null || h === undefined) return "";
    if (h < 1) return `${Math.round(h * 60)} min`;
    if (h < 48) return `${h.toFixed(1)} h`;
    return `${(h / 24).toFixed(1)} days`;
};
const pctOrDash = (v: number | null | undefined, dp = 1) => (v === null || v === undefined ? "-" : `${fmt(v, dp)}%`);

function rangeToDates(r: RangeSel): { start: string; end: string } {
    const today = new Date();
    const end = iso(today);
    if (r.kind === "preset") {
        const s = new Date(today); s.setUTCDate(s.getUTCDate() - (r.days - 1));
        return { start: iso(s), end };
    }
    if (r.kind === "custom") return { start: r.start, end: r.end };
    return { start: LIVE_DATE, end };
}

// Funnel rows that are results rather than the next step in the sequence:
// someone can come back, or subscribe, without ever opening a workout.
const OUTCOMES = new Set(["Came back another day", "Started a trial or paid"]);
const CANCELLED = "Cancelled the store sheet";

// Cohort heatmap: sage deepening with the share, an inset grey for a true 0%,
// nothing at all for weeks the cohort hasn't reached yet.
const heat = (v: number | null | undefined) => {
    if (v === null || v === undefined) return "transparent";
    if (v === 0) return C.inset;
    return `rgba(144,155,123,${Math.min(1, 0.15 + (v / 61) * 0.85).toFixed(2)})`;
};

// Sankey node: a labelled bar; labels sit to the right of the bar in every
// column except the last, where they sit to its left.
function FlowNode({ x, y, width, height, payload, lastStep }: any) {
    const right = payload.step < lastStep;
    const ended = payload.name === "Session ended";
    return (
        <g>
            <rect x={x} y={y} width={width} height={Math.max(2, height)} fill={ended ? C.neutral : C.sageD} rx={2} />
            <text x={right ? x + width + 8 : x - 8} y={y + height / 2} dy="0.35em" textAnchor={right ? "start" : "end"}
                style={{ fontSize: 13, fontWeight: 600, fontFamily: FONT, fill: C.ink }}>
                {pretty(payload.name)} <tspan style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 500 }} fill={C.muted}>{fmt(payload.value)}</tspan>
            </text>
        </g>
    );
}

// Sankey band: sage between screens, neutral into "Session ended".
function FlowLink({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload }: any) {
    const ended = payload?.target?.name === "Session ended";
    return (
        <path d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
            fill="none" stroke={ended ? "rgba(201,198,188,.35)" : "rgba(144,155,123,.34)"} strokeWidth={Math.max(1, linkWidth)} />
    );
}

// A funnel step with the drop from the step before underneath.
const FUNNEL_COLS = "minmax(110px, 200px) minmax(0, 1fr) auto";
function FunnelStep({ s, prev, base }: { s: Row; prev: Row | null; base: number }) {
    const share = base ? (s.users / base) * 100 : 0;
    const stepConv = prev && prev.users ? (s.users / prev.users) * 100 : null;
    const lost = prev ? prev.users - s.users : 0;
    return (<>
        <div style={{ display: "grid", gridTemplateColumns: FUNNEL_COLS, gap: 16, alignItems: "center", padding: "8px 0" }}>
            <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13 }}>{s.step}</div>
            <div style={{ height: 26, background: "rgba(22,25,37,.05)", borderRadius: 6 }}>
                <div style={{ height: 26, borderRadius: 6, background: C.sage, width: `${Math.min(100, share)}%`, minWidth: 4 }} />
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "baseline", justifyContent: "flex-end", minWidth: 76 }}>
                <span style={{ ...numStyle(20), lineHeight: 1 }}>{fmt(s.users)}</span>
                <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: C.muted }}>{fmt(share)}%</span>
            </div>
        </div>
        {(stepConv !== null || (s.median_hours !== null && s.median_hours !== undefined)) && (
            <div style={{ display: "grid", gridTemplateColumns: FUNNEL_COLS, gap: 16, fontFamily: FONT, fontSize: 11, color: C.muted, margin: "-2px 0 2px" }}>
                <div />
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {lost > 0 && <span style={{ fontWeight: 600, color: C.alert }}>{"−"}{fmt(lost)} dropped</span>}
                    {stepConv !== null && <span>{fmt(stepConv)}% of the step before</span>}
                </div>
                <div style={{ textAlign: "right", minWidth: 76 }}>{s.median_hours !== null && s.median_hours !== undefined ? `median ${hrs(s.median_hours)}` : ""}</div>
            </div>
        )}
    </>);
}

const SECTIONS = [
    { id: "engagement", n: "01", label: "Engagement" },
    { id: "navigation", n: "02", label: "Navigation" },
    { id: "funnels", n: "03", label: "Funnels" },
    { id: "works", n: "04", label: "What works" },
    { id: "notifications", n: "05", label: "Notifications" },
];

export default function InsightsDashboard() {
    const [range, setRange] = useState<RangeSel>({ kind: "preset", days: 30 });
    const [data, setData] = useState<Row | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [screenSort, setScreenSort] = useState<"views" | "users" | "avg_secs" | "exit_pct">("views");
    const { start, end } = useMemo(() => rangeToDates(range), [range]);

    useEffect(() => {
        setLoading(true);
        api.get(`/founder-portal/insights/?start=${start}&end=${end}`)
            .then(res => { setData(res.data); setErr(null); })
            .catch(e => setErr(e?.response?.data?.detail || e?.message || "Failed to load"))
            .finally(() => setLoading(false));
    }, [start, end]);

    const screens = useMemo(() => {
        const rows = [...(data?.screens || [])];
        return rows.sort((a, b) => (b[screenSort] ?? -1) - (a[screenSort] ?? -1)).slice(0, 15);
    }, [data, screenSort]);
    const maxViews = useMemo(() => Math.max(1, ...(data?.screens || []).map((s: Row) => s.views)), [data]);
    const daily: Row[] = useMemo(() => data?.daily || [], [data]);

    const right = (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>
                {shortDate(start)} – {shortDate(end)}{loading ? "  ·  updating…" : ""}
            </span>
            <RangePicker range={range} onChange={setRange} presets={[7, 30, 90]} />
        </div>
    );

    if (err) return <Shell title="Insights" right={right}><Empty>{err}</Empty></Shell>;

    const cov = data?.coverage || {};
    const t = data?.tiles || {};
    const act: Row[] = data?.activation || [];
    const pay: Row[] = data?.paywall || [];
    const eventsLive = !!cov.events_since;
    const flows = data?.flows;
    const hasFlows = flows && flows.links && flows.links.length > 0;

    // Share of the plotted days that fall before the request log began.
    const preRecording = (() => {
        if (!cov.requests_since || !daily.length) return 0;
        const before = daily.filter(d => d.date < cov.requests_since).length;
        return before / daily.length;
    })();

    const steps = act.filter(s => !OUTCOMES.has(s.step));
    const outcomes = act.filter(s => OUTCOMES.has(s.step));
    const base = act.length ? act[0].users : 0;
    const cohorts: Row[] = data?.cohorts || [];
    const cohortWeeks = Math.min(9, Math.max(1, ...cohorts.map(c =>
        (c.weeks || []).reduce((m: number, v: any, i: number) => (v !== null && v !== undefined ? i + 1 : m), 0))));
    const peakHour = (data?.by_hour || []).reduce((best: Row | null, h: Row) => (!best || h.sessions > best.sessions ? h : best), null);
    const progs: Row[] = data?.programme_families || [];
    const types: Row[] = data?.activity_types || [];

    return (
        <Shell title="Insights" right={right}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                    ["App requests", cov.requests_since],
                    ["Screen events", cov.events_since],
                    ["Daily opens", cov.daily_active_since],
                ].map(([label, since]) => (
                    <div key={label} style={{
                        display: "flex", gap: 6, alignItems: "center", fontFamily: FONT, fontWeight: 500, fontSize: 12,
                        color: "rgba(22,25,37,.7)", background: "#FFF", border: "1px solid rgba(22,25,37,.1)", padding: "5px 11px", borderRadius: 999,
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: since ? C.sage : C.alert }} />
                        {label}: {since ? `recording since ${shortDate(since)}` : "not recording yet"}
                    </div>
                ))}
            </div>

            {!data ? <div style={{ color: C.muted, fontFamily: FONT }}>Loading insights…</div> : <>

                <KpiStrip items={[
                    { label: "Active users", value: fmt(t.active_users), sub: "used the app in range" },
                    { label: "Sessions", value: fmt(t.sessions), sub: `${fmt(t.sessions_per_user, 1)} per user` },
                    { label: "Median session", value: dur(t.median_session_secs), sub: "first to last activity" },
                    { label: "Screens / session", value: fmt(t.screens_per_session, 1), sub: eventsLive ? "from screen events" : "from app requests" },
                    { label: "Sign-ups", value: fmt(t.registrations), sub: "registered in range" },
                    { label: "Trial or paid", value: t.conversion_pct === null || t.conversion_pct === undefined ? "-" : `${fmt(t.conversion_pct, 1)}%`, sub: "of those sign-ups, ever" },
                ]} />

                <SectionNav items={SECTIONS} />

                {/* ---------------- 01 engagement */}
                <SectionHead id="engagement" n="01" label="Engagement" title="How much people use the app" />
                <Card title="Daily active users and sessions"
                    right={<Legend items={[{ label: "Sessions", color: C.sageL2 }, { label: "Active users", color: C.ink, kind: "line" }]} />}>
                    <div style={{ position: "relative" }}>
                        {preRecording > 0.04 && (
                            <div style={{
                                position: "absolute", top: 0, bottom: 24, left: 34, zIndex: 1, pointerEvents: "none",
                                width: `calc((100% - 38px) * ${preRecording.toFixed(3)})`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: C.tick, background: C.inset, borderRadius: 999, padding: "5px 12px", whiteSpace: "nowrap" }}>
                                    Not recording before {shortDate(cov.requests_since)}
                                </div>
                            </div>
                        )}
                        <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={daily} margin={{ top: 6, right: 4, bottom: 0, left: 0 }} barCategoryGap="16%">
                                <CartesianGrid {...gridProps} />
                                <XAxis {...axisX} dataKey="date" tickFormatter={shortDate} minTickGap={28} />
                                <YAxis {...axisY} allowDecimals={false} />
                                <Tooltip {...tipBar} labelFormatter={shortDate} />
                                <Bar dataKey="sessions" name="Sessions" fill={C.sageL2} radius={[4, 4, 0, 0]} />
                                <Line dataKey="users" name="Active users" stroke={C.ink} strokeWidth={2} dot={false} strokeLinejoin="round" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Grid min={420}>
                    <Card title="How long sessions last">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={data.session_length} margin={{ top: 20, right: 4, bottom: 0, left: -6 }} barCategoryGap="18%">
                                <CartesianGrid {...gridProps} />
                                <XAxis {...axisX} dataKey="bucket" interval={0} />
                                <YAxis {...axisY} allowDecimals={false} />
                                <Tooltip {...tipBar} />
                                <Bar dataKey="sessions" name="Sessions" fill={C.sage} radius={[5, 5, 0, 0]}>
                                    <LabelList dataKey="sessions" position="top" style={{ ...numStyle(13), fill: C.ink }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                    <Card title="When people open the app"
                        right={peakHour && peakHour.sessions > 0 ? (
                            <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: "rgba(22,25,37,.6)" }}>
                                Peak <span style={{ ...numStyle(16), color: C.ink }}>{String(peakHour.hour).padStart(2, "0")}:00</span>
                            </span>
                        ) : undefined}>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={data.by_hour} margin={{ top: 6, right: 4, bottom: 0, left: -6 }} barCategoryGap="14%">
                                <CartesianGrid {...gridProps} />
                                <XAxis {...axisX} dataKey="hour" interval={5} tickFormatter={(h: number) => `${String(h).padStart(2, "0")}:00`} />
                                <YAxis {...axisY} allowDecimals={false} />
                                <Tooltip {...tipBar} labelFormatter={(h: any) => `${String(h).padStart(2, "0")}:00 - ${String(h).padStart(2, "0")}:59`} />
                                <Bar dataKey="sessions" name="Sessions" radius={[3, 3, 0, 0]}>
                                    {(data.by_hour || []).map((h: Row) => (
                                        <Cell key={h.hour} fill={peakHour && h.hour === peakHour.hour ? C.sageD : C.sage} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Grid>

                {/* ---------------- 02 navigation */}
                <SectionHead id="navigation" n="02" label="Navigation" title="Where people go and how they move" />
                <Card title="Screens" gap={12}>
                    {screens.length ? (
                        <TableWrap minWidth={460}>
                            <thead>
                                <tr>
                                    <th style={th()}>Screen</th>
                                    {([["views", "Views"], ["users", "People"], ["avg_secs", "Avg"], ["exit_pct", "Exit"]] as const).map(([k, l]) => (
                                        <th key={k} onClick={() => setScreenSort(k)} style={{ ...th("right", screenSort === k), cursor: "pointer", userSelect: "none", width: 72 }}>
                                            {l}{screenSort === k ? " ↓" : ""}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {screens.map((s: Row) => (
                                    <tr key={s.screen} className="hq-row">
                                        <td style={{ ...td(), padding: "8px 8px", minWidth: 160 }}>
                                            <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pretty(s.screen)}</div>
                                            <div style={{ height: 4, borderRadius: 4, background: C.track, marginTop: 5 }}>
                                                <div style={{ height: 4, borderRadius: 4, background: C.sage, width: `${(s.views / maxViews) * 100}%` }} />
                                            </div>
                                        </td>
                                        <td style={tdNum()}>{fmt(s.views)}</td>
                                        <td style={tdNum(500, "rgba(22,25,37,.7)")}>{fmt(s.users)}</td>
                                        <td style={tdNum(500, "rgba(22,25,37,.7)")}>{dur(s.avg_secs)}</td>
                                        <td style={tdNum(600, (s.exit_pct || 0) >= 50 ? C.alert : C.ink)}>{s.exit_pct === null ? "-" : `${fmt(s.exit_pct)}%`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </TableWrap>
                    ) : <Empty>No screen activity in this range.</Empty>}
                </Card>
                <Card title="Paths through a session"
                    right={<Legend items={[{ label: "Screen", color: C.sageD }, { label: "Session ended", color: C.neutral }]} />}>
                    {hasFlows ? (
                        <div style={{ width: "100%", overflowX: "auto" }}>
                            <div style={{ minWidth: 900 }}>
                                <ResponsiveContainer width="100%" height={540}>
                                    <Sankey data={flows} nodePadding={26} nodeWidth={10}
                                        margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                                        link={<FlowLink />}
                                        node={<FlowNode lastStep={Math.max(...flows.nodes.map((n: Row) => n.step))} />}>
                                        <Tooltip {...tipBar} />
                                    </Sankey>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : <Empty>No multi-screen sessions in this range yet.</Empty>}
                </Card>

                {/* ---------------- 03 funnels */}
                <SectionHead id="funnels" n="03" label="Funnels" title="Where new members drop out" />
                <Grid min={520}>
                    <Card title={act.length ? `From sign-up to first workout (${fmt(t.funnel_cohort)} people)` : "From sign-up to first workout"}>
                        {act.length ? (<>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                {steps.map((s, i) => (
                                    <FunnelStep key={s.step} s={s} prev={i ? steps[i - 1] : null} base={base} />
                                ))}
                            </div>
                            {outcomes.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 14, borderTop: "1px solid rgba(22,25,37,.1)" }}>
                                    <div style={{ ...microLabel, color: C.tick }}>Outcomes, whatever the path</div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 10 }}>
                                        {outcomes.map(o => (
                                            <div key={o.step} style={{ background: C.panel, borderRadius: 12, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                    <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13 }}>{o.step}</div>
                                                    {o.median_hours !== null && o.median_hours !== undefined && (
                                                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>median {hrs(o.median_hours)} after sign-up</div>
                                                    )}
                                                </div>
                                                <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                                                    <span style={{ ...numStyle(26), lineHeight: 1 }}>{fmt(o.users)}</span>
                                                    <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: C.muted }}>{fmt(base ? (o.users / base) * 100 : 0)}%</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>) : <Empty>The request log starts {cov.requests_since ? shortDate(cov.requests_since) : "once the backfill runs"}: pick a range from then.</Empty>}
                    </Card>
                    <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
                        <Card title="The paywall">
                            {pay.length ? pay.map((s, i) => {
                                const prev = i && s.step !== CANCELLED && pay[i - 1].step !== CANCELLED ? pay[i - 1] : null;
                                const share = base ? (s.users / base) * 100 : 0;
                                const stepConv = prev && prev.users ? (s.users / prev.users) * 100 : null;
                                const lost = prev ? prev.users - s.users : 0;
                                return (
                                    <div key={s.step} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                                            <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 13 }}>{s.step}</div>
                                            <div style={{ display: "flex", gap: 6, alignItems: "baseline", whiteSpace: "nowrap" }}>
                                                <span style={{ ...numStyle(18), lineHeight: 1 }}>{fmt(s.users)}</span>
                                                <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 11, color: C.muted }}>{fmt(share)}%</span>
                                            </div>
                                        </div>
                                        <div style={{ height: 6, borderRadius: 6, background: C.track }}>
                                            <div style={{ height: 6, borderRadius: 6, background: s.step === CANCELLED ? C.alert : C.ink, width: `${Math.min(100, share)}%` }} />
                                        </div>
                                        {stepConv !== null && (
                                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                                {fmt(stepConv)}% of the step before
                                                {lost > 0 && <> · <span style={{ fontWeight: 600, color: C.alert }}>{"−"}{fmt(lost)} dropped</span></>}
                                            </div>
                                        )}
                                    </div>
                                );
                            }) : <Empty>Needs the request log to cover the range.</Empty>}
                        </Card>
                        <Card title="Do new members come back?" gap={8}>
                            {cohorts.length ? (
                                <div style={{ overflowX: "auto" }}>
                                    <div style={{ minWidth: 120 + cohortWeeks * 46, display: "flex", flexDirection: "column", gap: 4 }}>
                                        <div style={{ display: "grid", gridTemplateColumns: `60px 40px repeat(${cohortWeeks}, minmax(40px, 1fr))`, gap: 4, ...microLabel, fontSize: 10, letterSpacing: "0.08em", color: C.tick }}>
                                            <div>Joined</div><div style={{ textAlign: "right" }}>n</div>
                                            {Array.from({ length: cohortWeeks }).map((_, k) => <div key={k} style={{ textAlign: "center" }}>Wk {k}</div>)}
                                        </div>
                                        {cohorts.map((c: Row) => (
                                            <div key={c.week} style={{ display: "grid", gridTemplateColumns: `60px 40px repeat(${cohortWeeks}, minmax(40px, 1fr))`, gap: 4, alignItems: "center" }}>
                                                <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12 }}>{shortDate(c.week)}</div>
                                                <div style={{ ...numStyle(14), textAlign: "right", color: "rgba(22,25,37,.6)" }}>{fmt(c.size)}</div>
                                                {Array.from({ length: cohortWeeks }).map((_, k) => {
                                                    const v = c.weeks[k];
                                                    return (
                                                        <div key={k} style={{
                                                            height: 34, borderRadius: 7, background: heat(v), ...numStyle(14),
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            color: v !== undefined && v !== null && v >= 40 ? "#FFF" : C.ink,
                                                        }}>{v === undefined || v === null ? "" : `${fmt(v)}%`}</div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : <Empty>No sign-ups in range since daily opens started recording.</Empty>}
                        </Card>
                    </div>
                </Grid>

                {/* ---------------- 04 what works */}
                <SectionHead id="works" n="04" label="What works" title="Programmes and workout types" />
                <Split>
                    <Card title="Completion by programme"
                        right={<Legend items={[{ label: "Workouts completed", color: C.sage }, { label: "Members active in last 7 days", color: C.slate }]} />}>
                        {progs.length ? (
                            <TableWrap minWidth={700}>
                                <thead>
                                    <tr>
                                        <th style={th()}>Plan</th>
                                        <th style={th("right")}>People</th>
                                        <th style={th("right")}>Due</th>
                                        <th style={th("right")}>Done</th>
                                        <th style={{ ...th(), width: "22%" }}>Done %</th>
                                        <th style={{ ...th(), width: "22%" }}>Active 7d</th>
                                        <th style={th("right")}>Trial / paid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {progs.map((r: Row) => (
                                        <tr key={r.name} className="hq-row">
                                            <td style={{ ...td(), fontWeight: 600 }}>{r.name}</td>
                                            <td style={tdNum(500)}>{fmt(r.users)}</td>
                                            <td style={tdNum(500)}>{fmt(r.due)}</td>
                                            <td style={tdNum(500)}>{fmt(r.completed)}</td>
                                            <td style={td()}>
                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <div style={{ flex: 1, height: 8, borderRadius: 8, background: C.track }}>
                                                        <div style={{ height: 8, borderRadius: 8, background: C.sage, width: `${Math.min(100, r.completion_pct || 0)}%` }} />
                                                    </div>
                                                    <span style={{ ...numStyle(15), width: 38, textAlign: "right" }}>{r.completion_pct === null ? "-" : `${fmt(r.completion_pct)}%`}</span>
                                                </div>
                                            </td>
                                            <td style={td()}>
                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <div style={{ flex: 1, height: 8, borderRadius: 8, background: C.track }}>
                                                        <div style={{ height: 8, borderRadius: 8, background: C.slate, width: `${Math.min(100, r.active_7d_pct || 0)}%` }} />
                                                    </div>
                                                    <span style={{ ...numStyle(13, 500), width: 38, textAlign: "right", color: "rgba(22,25,37,.6)" }}>{r.active_7d_pct === null || r.active_7d_pct === undefined ? "-" : `${fmt(r.active_7d_pct)}%`}</span>
                                                </div>
                                            </td>
                                            <td style={tdNum()}>{r.subscribed_pct === null ? "-" : `${fmt(r.subscribed_pct)}%`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </TableWrap>
                        ) : <Empty>No scheduled workouts in this range.</Empty>}
                    </Card>
                    <Card title="Completion by workout type" gap={12}>
                        {types.length ? types.map((r: Row) => (
                            <BarRow key={r.name} label={r.name} value={pctOrDash(r.completion_pct, 0)} pct={r.completion_pct || 0}
                                color={discColor(r.name)} dot={discColor(r.name)} labelWidth={74} valueWidth={46} />
                        )) : <Empty>No scheduled workouts in this range.</Empty>}
                    </Card>
                </Split>

                {/* ---------------- 05 notifications */}
                {data.notifications && (() => {
                    const n = data.notifications as Row;
                    const nt = n.tiles || {};
                    const ov = n.overall as Row | null;
                    const tapsLive = (n.types || []).some((r: Row) => r.tap_measured > 0);
                    const groups: Row[] = n.groups || [];
                    const groupMax = Math.max(1, ...groups.map(g => Math.max(g.open_pct || 0, g.baseline_pct || 0)));
                    const liftMax = Math.max(1, ...(n.types || []).map((r: Row) => Math.abs(r.lift_pts || 0)));
                    return (<>
                        <SectionHead id="notifications" n="05" label="Notifications" title="What notifications do"
                            intro={<>An open is the person using the app within an hour of the push, compared with how often they open it at that time of day on nearby days with no push.
                                Pushes that land while the app is already in use are left out. {tapsLive ? "Taps are the app reporting the notification itself being tapped." : "Real taps appear once the analytics app update is live."}</>} />
                        <KpiStrip items={[
                            { label: "Sent", value: fmt(nt.sent), sub: `to ${fmt(nt.recipients)} people` },
                            { label: "Opened within 1h", value: pctOrDash(ov?.open_pct), sub: `usually ${pctOrDash(ov?.baseline_pct)} at that time`, color: C.sageD },
                            { label: "Recipients not using the app", value: nt.recipients ? `${fmt((nt.recipients_inactive / nt.recipients) * 100)}%` : "-", sub: `${fmt(nt.recipients_inactive)} got pushes, never opened it in range`, color: C.alert },
                            { label: "Active users we can't reach", value: nt.active_users ? `${fmt((nt.active_unreachable / nt.active_users) * 100)}%` : "-", sub: `${fmt(nt.active_unreachable)} of ${fmt(nt.active_users)} have no push token`, color: C.alert },
                            { label: "Can receive pushes", value: fmt(nt.pushable_users), sub: `of ${fmt(nt.total_users)} accounts` },
                        ]} />
                        <Grid min={520}>
                            <Card title="Open rate by group"
                                right={<Legend items={[{ label: "After the push", color: C.ink }, { label: "Usual at that time", color: C.watch }]} />} gap={6}>
                                {groups.length ? groups.map(g => (
                                    <div key={g.name} style={{ display: "grid", gridTemplateColumns: "minmax(100px, 140px) minmax(0, 1fr)", gap: 12, alignItems: "center", padding: "7px 0", borderTop: `1px solid ${C.grid}` }}
                                        title={`${g.name}: ${pctOrDash(g.open_pct)} after (${fmt(g.opened)} of ${fmt(g.measured)}), usually ${pctOrDash(g.baseline_pct)}`}>
                                        <div style={{ display: "flex", gap: 8, alignItems: "center", fontFamily: FONT, fontWeight: 600, fontSize: 13 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: 2, background: ngroupColor(g.name), flexShrink: 0 }} />{g.name}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <div style={{ height: 10, borderRadius: 3, background: C.ink, width: `${((g.open_pct || 0) / groupMax) * 85}%`, minWidth: 2 }} />
                                                <span style={numStyle(14)}>{pctOrDash(g.open_pct)}</span>
                                            </div>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <div style={{ height: 10, borderRadius: 3, background: C.watch, width: `${((g.baseline_pct || 0) / groupMax) * 85}%`, minWidth: 2 }} />
                                                <span style={{ ...numStyle(13, 500), color: C.muted }}>{pctOrDash(g.baseline_pct)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )) : <Empty>No notifications sent in this range.</Empty>}
                            </Card>
                            <Card title="Open rate by send time"
                                right={<Legend items={[{ label: "Opened 1h", color: C.sage }, { label: "Pushes sent", color: C.ink, kind: "line" }]} />}>
                                <ResponsiveContainer width="100%" height={240}>
                                    <ComposedChart data={(n.by_hour || []).map((h: Row) => ({ ...h, open_pct: h.measured >= 10 ? h.open_pct : null }))} margin={{ top: 6, right: 0, bottom: 0, left: -2 }} barCategoryGap="14%">
                                        <CartesianGrid {...gridProps} />
                                        <XAxis {...axisX} dataKey="hour" interval={5} tickFormatter={(h: number) => `${String(h).padStart(2, "0")}:00`} />
                                        <YAxis {...axisY} yAxisId="pct" tick={{ ...axisY.tick, fill: C.sageD }} tickFormatter={(v: number) => `${fmt(v)}%`} />
                                        <YAxis {...axisY} yAxisId="n" orientation="right" width={40} allowDecimals={false} tickFormatter={(v: number) => fmt(v)} />
                                        <Tooltip {...tipBar} labelFormatter={(h: any) => `Sent ${String(h).padStart(2, "0")}:00 - ${String(h).padStart(2, "0")}:59`}
                                            formatter={(v: any, name: any) => [name === "Opened" ? pctOrDash(v) : fmt(v), name]} />
                                        <Bar yAxisId="pct" dataKey="open_pct" name="Opened" fill={C.sage} radius={[3, 3, 0, 0]} />
                                        <Line yAxisId="n" dataKey="measured" name="Pushes sent" stroke={C.ink} strokeWidth={2} dot={false} strokeLinejoin="round" />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Card>
                        </Grid>
                        <Card title="By notification type">
                            {(n.types || []).length ? (
                                <TableWrap minWidth={980}>
                                    <thead>
                                        <tr>
                                            <th style={th()}>Notification</th>
                                            <th style={th("right")}>Sent</th>
                                            <th style={th("right")}>Opened in 1h</th>
                                            <th style={th("right")}>Usual</th>
                                            <th style={{ ...th("center"), width: 170 }}>Lift</th>
                                            <th style={th("right")}>Tapped</th>
                                            <th style={th("right")}>Usually sent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(n.types || []).map((r: Row, i: number, all: Row[]) => {
                                            const lift = r.lift_pts;
                                            const w = lift === null || lift === undefined ? 0 : (Math.abs(lift) / liftMax) * 100;
                                            return (
                                                <React.Fragment key={r.name}>
                                                    {(i === 0 || all[i - 1].group !== r.group) && (
                                                        <tr>
                                                            <td colSpan={7} style={{ padding: "16px 8px 6px", borderBottom: "1px solid rgba(22,25,37,.1)" }}>
                                                                <span style={{ display: "flex", gap: 8, alignItems: "center", ...microLabel, fontWeight: 700, letterSpacing: "0.14em", color: C.ink }}>
                                                                    <span style={{ width: 8, height: 8, borderRadius: 2, background: ngroupColor(r.group) }} />{r.group}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    <tr className="hq-row">
                                                        <td style={{ ...td(), fontWeight: 500 }}>{r.name}</td>
                                                        <td style={tdNum()}>{fmt(r.sent)}</td>
                                                        <td style={tdNum()}>
                                                            {pctOrDash(r.open_pct)} <span style={{ fontFamily: FONT, fontWeight: 400, fontSize: 11, color: C.tick }}>{r.measured ? `${fmt(r.opened)}/${fmt(r.measured)}` : ""}</span>
                                                        </td>
                                                        <td style={tdNum(500, C.muted)}>{pctOrDash(r.baseline_pct)}</td>
                                                        <td style={td()}>
                                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "center", position: "relative", height: 18 }}>
                                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                                    {lift !== null && lift < 0 && <div style={{ height: 8, background: C.alert, width: `${w}%`, borderRadius: "3px 0 0 3px" }} />}
                                                                </div>
                                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                    {lift !== null && lift > 0 && <div style={{ height: 8, background: C.sageD, width: `${w * 0.7}%`, borderRadius: "0 3px 3px 0" }} />}
                                                                    <span style={{ ...numStyle(14), color: lift === null ? C.muted : lift > 0 ? C.sageD : lift < 0 ? C.alert : C.ink, paddingLeft: lift !== null && lift > 0 ? 0 : 6 }}>
                                                                        {lift === null ? "-" : `${lift > 0 ? "+" : ""}${fmt(lift, 1)}`}
                                                                    </span>
                                                                </div>
                                                                <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, borderLeft: "1px solid rgba(22,25,37,.3)" }} />
                                                            </div>
                                                        </td>
                                                        <td style={tdNum(500)}>{r.tap_measured ? pctOrDash(r.tap_pct) : "-"}</td>
                                                        <td style={tdNum(500, "rgba(22,25,37,.6)")}>{r.usual_hour === null ? "-" : `${String(r.usual_hour).padStart(2, "0")}:00`}</td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </TableWrap>
                            ) : <Empty>No notifications sent in this range.</Empty>}
                        </Card>
                        <Card title="Notifications sent per day"
                            right={<Legend items={(n.group_names || []).map((g: string) => ({ label: g, color: ngroupColor(g) }))} />}>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={n.daily || []} margin={{ top: 6, right: 4, bottom: 0, left: -2 }} barCategoryGap="16%">
                                    <CartesianGrid {...gridProps} />
                                    <XAxis {...axisX} dataKey="date" tickFormatter={shortDate} minTickGap={28} />
                                    <YAxis {...axisY} allowDecimals={false} />
                                    <Tooltip {...tipBar} labelFormatter={shortDate} />
                                    {(n.group_names || []).map((g: string, i: number, arr: string[]) => (
                                        <Bar key={g} dataKey={g} stackId="g" fill={ngroupColor(g)} stroke="#FFF" strokeWidth={1}
                                            radius={i === arr.length - 1 ? [4, 4, 0, 0] : undefined} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </>);
                })()}
            </>}
        </Shell>
    );
}

